import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { CompetencyStatus, UserCompetency, WorkOrderCompetency } from 'app/_models/competency.model';
import { TrainingCourseState } from 'app/_models/training/training-course-state';
import { CompetencyService } from 'app/_services/competency.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteSessionsService } from 'app/_services/site-sessions.service';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import { SiteService } from 'app/_services/site.service';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ChooseWorkOrderModalComponent } from './choose-work-order-modal/choose-work-order-modal.component';
import { ChooseWorkersModalComponent } from './choose-workers-modal/choose-workers-modal.component';

@Component({
  selector: 'check-in',
  templateUrl: './check-in.component.html',
  providers: [DatePipe],
})
export class CheckInComponent implements OnInit {
  public routeRole: any;

  public selectedSiteId: any;
  public selectedSite: any;

  private preselectedWorkOrderId: number;
  private preselectedWorkerId: number;

  public isSiteLoading: boolean = true;

  public workOrder: any;
  public selectedWorkers: any = [];
  public isSiteSession: boolean;
  public isFormBeingSubmitted: boolean;
  public checkOnForm: any;
  public leadWorkerIndex: number = -1;

  public TrainingCourseStateType = TrainingCourseState; // allow enum in template
  public minDate: Date = new Date();
  private defaultCheckOffTime: Date;
  public allowAssetScheduling: boolean;
  public isTocSite: boolean;
  public assetScheduleSlotCurrentlyAvailable: any;
  public workOrderCompetencies: WorkOrderCompetency[] = [];
  public leadWorkerCompetencies: WorkOrderCompetency[] = [];
  public allWorkerCompetencies: WorkOrderCompetency[] = [];
  public workOrderLoading: boolean = false;
  public workOrderError: boolean = false;
  public workOrderCompetenciesLoading: boolean = false;
  public workOrderCompetenciesError: boolean = false;
  public errorAlerts: any[] = [];
  public preemptiveErrorAlerts: any[] = [];
  public restrictionsPreviewCount: number = 3;
  public showAllAssetRestrictions: boolean = false;
  public showAllSiteRestrictions: boolean = false;
  private workOrderRequestId: number = 0;
  private workOrderCompetencyRequestId: number = 0;
  private workerCompetencyRequestId: number = 0;

  constructor(
    private activatedRoute: ActivatedRoute,
    private modalService: NgbModal,
    private siteService: SiteService,
    private workOrderService: SiteWorkOrdersService,
    private competencyService: CompetencyService,
    private siteSessionsService: SiteSessionsService,
    private notificationService: NotificationService,
    private router: Router,
    private datepipe: DatePipe,
    globals: Globals,
    fb: FormBuilder,
  ) {
    this.routeRole = globals.user.routeRole;

    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
    });

    this.activatedRoute.queryParams.subscribe((params: Params) => {
      this.preselectedWorkOrderId = params.workOrderId ? parseInt(params.workOrderId) : undefined;
      this.preselectedWorkerId = params.workerId ? parseInt(params.workerId) : undefined;
    });

    this.defaultCheckOffTime = new Date();
    this.defaultCheckOffTime.setHours(this.defaultCheckOffTime.getHours() + 3);

    const customValidator: ValidatorFn = (formGroup: FormGroup): ValidationErrors | null => {
      const isSiteSession = formGroup.get('isSiteSession').value;
      const workOrder = formGroup.get('workOrder').value;

      if (!isSiteSession && workOrder === null) {
        return { invalidCombo: true };
      }

      return null;
    };

    this.checkOnForm = fb.group({
      isSiteSession: new FormControl(false),
      reason: new FormControl('', [Validators.required]),
      workOrder: new FormControl(),
      skipAssetAvailabilityCheck: new FormControl(false),
      guestCount: new FormControl(0),
      checkOffTime: new FormControl(this.defaultCheckOffTime, [Validators.required]),
    }, { validator: customValidator });
  }

  ngOnInit(): void {
    this.setupPage();
  }

  public get areCompetenciesKnown(): boolean {
    const workerCompetenciesKnown = this.selectedWorkers.every((worker) => !worker.competenciesLoading && !worker.competenciesError);

    if (!workerCompetenciesKnown) {
      return false;
    }

    if (this.checkOnForm.value.isSiteSession) {
      return true;
    }

    if (this.workOrderLoading || this.workOrderError || !this.checkOnForm.value.workOrder) {
      return false;
    }

    return !this.workOrderCompetenciesLoading && !this.workOrderCompetenciesError;
  }

  public setupPage() {
    this.siteService.getSiteById(this.selectedSiteId).subscribe(data => {
      this.selectedSite = data
      this.isSiteLoading = false;

      this.allowAssetScheduling = this.selectedSite.siteOwner.siteOwnerUser.allowAssetScheduling;
      this.isTocSite = this.selectedSite.siteOwner.siteOwnerUser.allowTocAcknowledgement;
      if (this.isTocSite) {
        this.isSiteSession = true;
        this.checkOnForm.patchValue({
          isSiteSession: true,
        });
      }

      if (this.preselectedWorkOrderId) {
        this.isSiteSession = false;
        this.startWorkOrderLoad();
        this.getWorkOrderDetails(this.preselectedWorkOrderId);
      }

      if (this.preselectedWorkerId) {
        this.getWorkerDetails(this.preselectedWorkerId);
      }
    }, error => {
      this.isSiteLoading = false;
      console.error(error);
      this.notificationService.notify('danger', 'Error', 'Problem loading site data');
    });
  }

  public editWorkOrder() {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'lg' };
    const modal = this.modalService.open(ChooseWorkOrderModalComponent, modalOptions);
    modal.componentInstance.site = this.selectedSite;
    modal.componentInstance.workOrder = this.workOrder;
    modal.componentInstance.isSiteSession = this.isSiteSession;

    modal.result.then(result => {
      if (result) {
        if (result.workOrder) {
          const workOrderId = result.workOrder.id;
          this.isSiteSession = false;
          this.startWorkOrderLoad();
          this.getWorkOrderDetails(workOrderId);
        } else if (result.isSiteSession) {
          this.workOrderRequestId += 1;
          this.resetWorkOrderState();
          this.isSiteSession = true;
          this.leadWorkerIndex = -1;
          this.checkOnForm.patchValue({
            workOrder: null,
            isSiteSession: true,
          });
        }
      }
    }).catch(() => { /* modal dismissed */ });
  }

  public editSelectedWorkers() {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'lg' };
    const modal = this.modalService.open(ChooseWorkersModalComponent, modalOptions);
    modal.componentInstance.site = this.selectedSite;
    // Pass worker object copies so modal mapping does not mutate the background view state.
    modal.componentInstance.selectedWorkers = this.selectedWorkers.map((worker) => ({
      ...worker,
      competencies: Array.isArray(worker.competencies) ? [...worker.competencies] : [],
    }));

    modal.result.then(async (result) => {
      if (result) {
        const availableWorkers = await this.filterWorkersAlreadyInSession(result);
        this.selectedWorkers = availableWorkers.map((worker) => ({
          ...worker,
          competencies: [],
          competenciesLoading: true,
          competenciesError: false,
        }));
        await this.loadSelectedWorkersCompetencies();

        if (availableWorkers.length && (!this.workOrder || this.workOrder.status !== 'OPEN')) {
          this.leadWorkerIndex = 0;
        } else {
          this.leadWorkerIndex = -1;
        }
        this.checkUsersForSessions();
      }
    }).catch(() => { /* modal dismissed */ });
  }

  private filterWorkersAlreadyInSession(workers: any[]): Promise<any[]> {
    return new Promise((resolve) => {
      if (!workers || !workers.length) {
        return resolve([]);
      }

      const checks = workers.map((worker) => {
        return this.siteSessionsService.checkIfUserInSession(worker.workerUser.id).pipe(
          map((response: any) => {
            const sessions = Array.isArray(response) ? response : (response && response.body) || [];
            return {
            worker,
            inSession: !!sessions.length,
            };
          }),
          catchError(() => of({
            worker,
            inSession: false,
          })),
        );
      });

      forkJoin(checks).subscribe((results: any[]) => {
        const blockedWorkers = results
          .filter((result) => result.inSession)
          .map((result) => `${result.worker.workerUser.firstName} ${result.worker.workerUser.lastName}`);

        if (blockedWorkers.length) {
          this.notificationService.notify(
            'warning',
            'Workers unavailable',
            `${blockedWorkers.join(', ')} already in a session and were not added.`,
          );
        }

        resolve(results.filter((result) => !result.inSession).map((result) => result.worker));
      });
    });
  }

  private getWorkOrderDetails(workOrderId: any) {
    const requestId = ++this.workOrderRequestId;
    this.workOrderLoading = true;
    this.workOrderError = false;
    this.showAllAssetRestrictions = false;
    this.showAllSiteRestrictions = false;
    this.workOrderService.getWorkOrderById(workOrderId).subscribe(data => {
      if (requestId !== this.workOrderRequestId) {
        return;
      }

      this.workOrderLoading = false;
      this.workOrder = data;
      this.loadWorkOrderCompetencies(workOrderId);

      this.checkOnForm.patchValue({
        workOrder: this.workOrder,
        isSiteSession: false,
      });

      if (this.workOrder.status === 'OPEN') {
        this.leadWorkerIndex = -1;
      } else if (this.leadWorkerIndex < 0 && this.selectedWorkers.length) {
        this.leadWorkerIndex = 0;
      }

      if (this.allowAssetScheduling) {
        this.prepareAssetScheduleInfo(this.workOrder.asset);
      }
    }, error => {
      if (requestId !== this.workOrderRequestId) {
        return;
      }

      this.resetWorkOrderState();
      this.checkOnForm.patchValue({
        workOrder: null,
        isSiteSession: false,
      });
      this.workOrderError = true;
      console.error(error);
      this.notificationService.notify('danger', 'Error', 'Problem loading work order data');
    });
  }

  private startWorkOrderLoad() {
    this.resetWorkOrderState();
    this.workOrderLoading = true;
    this.checkOnForm.patchValue({
      workOrder: null,
      isSiteSession: false,
    });
  }

  private resetWorkOrderState() {
    this.workOrder = null;
    this.workOrderLoading = false;
    this.workOrderError = false;
    this.clearWorkOrderCompetencies();
  }

  private loadWorkOrderCompetencies(workOrderId: number) {
    const requestId = ++this.workOrderCompetencyRequestId;
    this.clearWorkOrderCompetencies();
    this.workOrderCompetenciesLoading = true;
    this.workOrderCompetenciesError = false;

    this.competencyService.getWorkOrderCompetencies(workOrderId).subscribe((competencies) => {
      if (requestId !== this.workOrderCompetencyRequestId || !this.workOrder || this.workOrder.id !== workOrderId) {
        return;
      }

      this.workOrderCompetencies = competencies || [];
      this.allWorkerCompetencies = this.workOrderCompetencies.filter((competency) => competency.applyToParty);
      this.leadWorkerCompetencies = this.workOrderCompetencies.filter((competency) => !competency.applyToParty);
      this.workOrderCompetenciesLoading = false;
    }, () => {
      if (requestId !== this.workOrderCompetencyRequestId || !this.workOrder || this.workOrder.id !== workOrderId) {
        return;
      }

      this.clearWorkOrderCompetencies();
      this.workOrderCompetenciesLoading = false;
      this.workOrderCompetenciesError = true;
      this.notificationService.notify('danger', 'Error', 'Problem loading work order competencies');
    });
  }

  private clearWorkOrderCompetencies() {
    this.workOrderCompetencies = [];
    this.leadWorkerCompetencies = [];
    this.allWorkerCompetencies = [];
    this.workOrderCompetenciesLoading = false;
    this.workOrderCompetenciesError = false;
  }

  private getWorkerDetails(workerId: any) {
    let workerData = localStorage.getItem('temp.data.checkInWorker');

    if (workerData) {
      let storedWorkerData = JSON.parse(workerData);
      if (storedWorkerData.id !== workerId) {
        return;
      }

      this.selectedWorkers = [{
        ...storedWorkerData,
        competencies: [],
        competenciesLoading: true,
        competenciesError: false,
      }];
      this.loadSelectedWorkersCompetencies();
      if (!this.workOrder || this.workOrder.status !== 'OPEN') {
        this.leadWorkerIndex = 0;
      }
      localStorage.removeItem('temp.data.checkInWorker');

      this.checkUsersForSessions();
    }
  }

  private prepareAssetScheduleInfo(asset) {
    let foundAvailable = false;
    asset.assetAvailability.forEach(window => {
      window.status = this.calculateAvailabilityWindowStatus(window);

      // don't show windows that already have work orders as available
      if (window.workOrderId) {
        window.status = 'Past';
      }

      // only show the first Available as Available
      if (window.status === 'Available') {
        if (foundAvailable) {
          window.status = 'Upcoming';
        } else {
          foundAvailable = true;
        }
      }
    });

    this.assetScheduleSlotCurrentlyAvailable = foundAvailable;
  }

  private calculateAvailabilityWindowStatus(item): string {
    const now = new Date();

    const earliestStart = new Date(item.startTime);
    const latestStart = new Date(item.startTime);
    earliestStart.setHours(earliestStart.getHours() - 1);
    latestStart.setHours(latestStart.getHours() + 1);

    if (now.getTime() < earliestStart.getTime()) {
      return 'Upcoming';
    } else if (latestStart.getTime() < now.getTime()) {
      return 'Past';
    } else {
      return 'Available';
    }
  }

  private checkUsersForSessions() {
    this.preemptiveErrorAlerts = [];
    return new Promise((resolve, reject) => {

    let requests = [];
      for (let i = 0; i < this.selectedWorkers.length; i++) {
        const worker = this.selectedWorkers[i];

        requests.push(
          this.siteSessionsService.checkIfUserInSession(worker.workerUser.id)
          .pipe(tap((sessions: any) => {
            if (sessions.length) {
              this.preemptiveErrorAlerts.push({ severity: 'error', summary: worker.workerUser.firstName + ' ' + worker.workerUser.lastName + ' is already in a session' });
            }
          }))
        );
      }

      forkJoin(requests).subscribe(() => {
        if (this.preemptiveErrorAlerts.length) {
          this.isFormBeingSubmitted = false;
          return resolve(false);
        } else {
          return resolve(true);
        }
      });
    });
  }

  public async onCheckOnFormSubmit() {
    this.isFormBeingSubmitted = true;
    this.errorAlerts = [];

    const values = this.checkOnForm.value;

    const areCompetenciesKnown = this.validateCompetencyStatesBeforeCheckIn(values);
    if (!areCompetenciesKnown) { return; }

    const isWorkPartyValid = await this.validateWorkerBeforeCheckIn(values);
    if (!isWorkPartyValid) { return; }

    const isWorkPartyAvailable = await this.checkUsersForSessions();
    if (!isWorkPartyAvailable) { return; }


    if (values.checkOffTime < new Date()) {
      this.isFormBeingSubmitted = false;
      this.errorAlerts.push({ severity: 'error', summary: 'Expected check-off time must be in the future' });
      return;
    }

    if (values.isSiteSession) {
      this.createASiteSession(values);
    } else {
      // is work order

      const shouldContinue1 = this.checkAssetSchedule(values);
      if (!shouldContinue1) { return; }

      const shouldContinue2 = await this.checkWorkOrderSchedule(values);
      if (!shouldContinue2) { return; }

      const shouldContinue3 = this.checkAdditionalWorkers(values);
      if (!shouldContinue3) { return; }

      if (this.workOrder.status === 'AVAILABLE') {
        this.startAnAvailableWorkOrderAndAddWorkers(values);
      } else if (this.workOrder.status === 'OPEN') {
        this.checkInWorkersToOpenWorkOrder(values);
      } else {
        this.startASuspendedOrSurrenderedWorkOrderAndAddWorkers(values);
      }
    }
  }

  private createASiteSession(values: any) {
    let requests = [];

    for (let i = 0; i < this.selectedWorkers.length; i++) {
      const worker = this.selectedWorkers[i];

      const body = {
        siteId: this.selectedSiteId,
        userSession: {
          guestCount: values.guestCount || 0,
          reason: values.reason,
          skylarkUserId: worker.workerUser.id,
          expectedCheckOffTime: values.checkOffTime,
          owner: true,
        },
        workOrderId: null,
      };

      requests.push(
        this.siteSessionsService.createManagerSiteSession(body)
        .pipe(tap(() => {
          this.notificationService.notify('success', 'Success', worker.workerUser.firstName + ' ' + worker.workerUser.lastName + ' Checked On');
          }), map((response) => {
          return {
            response,
            workerId: worker.id,
          };
          }))
        .pipe(catchError((error) => {
          error.error.title = worker.workerUser.firstName + ' ' + worker.workerUser.lastName + ' - ' + error.error.title;
          return Observable.of(error);
        }))
      );
    }

    forkJoin(requests).subscribe((data: any) => {
      let errorCount = 0;
      data.forEach(response => {
        if (response instanceof HttpErrorResponse) {
          errorCount++;
          this.propagateErrors(response);
        } else {
          this.selectedWorkers = this.selectedWorkers.filter(worker => worker.id !== response.workerId);
        }
      });

      this.isFormBeingSubmitted = false;
      if (errorCount === 0) {
        this.notificationService.notify('success', 'Success', 'Site Sessions Created and worker Checked On');
        this.router.navigate(['/' + this.routeRole + '/live-sessions'], { queryParams: { tab: 'list' } });
      }
    });
  }

  private startAnAvailableWorkOrderAndAddWorkers(values: any) {
    let leadWorkerRequest;
    let additionalWorkers = [];

    for (let i = 0; i < this.selectedWorkers.length; i++) {
      const worker = this.selectedWorkers[i];

      if (i === this.leadWorkerIndex) {
        const body = {
          siteId: this.selectedSiteId,
          userSession: {
            guestCount: 0,
            reason: values.reason,
            skylarkUserId: worker.workerUser.id,
            expectedCheckOffTime: values.checkOffTime,
            owner: true,
          },
          skipAssetAvailabilityCheck: values.skipAssetAvailabilityCheck,
          workOrderId: this.workOrder.id,
        };

        leadWorkerRequest = this.siteSessionsService.createSiteSession(body);
      } else {
        const body = {
          siteSessionId: undefined,
          joinExistingWorkParty: true,
          userSession: {
            guestCount: 0,
            reason: values.reason,
            skylarkUserId: worker.workerUser.id,
            expectedCheckOffTime: values.checkOffTime,
            owner: false,
          },
        };

        additionalWorkers.push(body);
      }
    }

    leadWorkerRequest
      .concatMap((siteSessionResponse) => {
        if (additionalWorkers.length === 0) {
          return of([]);
        }

        additionalWorkers.forEach(worker => {
          worker.siteSessionId = siteSessionResponse.id;
        });

        let requests = additionalWorkers.map(worker => this.siteSessionsService.checkIntoExistingSession(worker));
        return forkJoin(requests);
      })
      .pipe(finalize(() => { this.isFormBeingSubmitted = false; }))
      .subscribe((data: any) => {
        this.checkOnForm.reset();
        this.notificationService.notify('success', 'Success', 'Session Created and worker Checked On.');
        this.router.navigate(['/' + this.routeRole + '/sites/', this.selectedSiteId, 'work-orders']);
      });
  }

  private checkInWorkersToOpenWorkOrder(values: any) {
    this.siteSessionsService.getSiteSessionForWorkOrder(values.workOrder.id).subscribe((siteSession: any) => {
      let additionalWorkers = [];

      for (let i = 0; i < this.selectedWorkers.length; i++) {
        const worker = this.selectedWorkers[i];

        const body = {
          siteSessionId: siteSession.id,
          joinExistingWorkParty: true,
          userSession: {
            guestCount: 0,
            reason: values.reason,
            skylarkUserId: worker.workerUser.id,
            expectedCheckOffTime: values.checkOffTime,
            owner: false,
          },
        };

        additionalWorkers.push(body);
      }

      let requests = additionalWorkers.map(worker => {
        return this.siteSessionsService.checkIntoExistingSession(worker);
      });

      forkJoin(requests)
        .pipe(finalize(() => { this.isFormBeingSubmitted = false; }))
        .subscribe((data: any) => {
          this.checkOnForm.reset();
          this.notificationService.notify('success', 'Success', 'Workers Checked On.');
          this.router.navigate(['/' + this.routeRole + '/sites/', this.selectedSiteId, 'work-orders']);
        });
    });
  }

  private startASuspendedOrSurrenderedWorkOrderAndAddWorkers(values: any) {
    this.siteSessionsService.getSiteSessionForWorkOrder(values.workOrder.id).subscribe((siteSession: any) => {

      let leadWorkerRequest;
      let additionalWorkers = [];

      for (let i = 0; i < this.selectedWorkers.length; i++) {
        const worker = this.selectedWorkers[i];

        if (i === this.leadWorkerIndex) {
          const body = {
            siteSessionId: siteSession.id,
            userSession: {
              guestCount: 0,
              reason: values.reason,
              skylarkUserId: worker.workerUser.id,
              expectedCheckOffTime: values.checkOffTime,
              owner: true,
            },
            skipAssetAvailabilityCheck: values.skipAssetAvailabilityCheck,
          };

          leadWorkerRequest = this.siteSessionsService.checkIntoExistingSession(body);
        } else {
          const body = {
            siteSessionId: siteSession.id,
            joinExistingWorkParty: true,
            userSession: {
              guestCount: 0,
              reason: values.reason,
              skylarkUserId: worker.workerUser.id,
              expectedCheckOffTime: values.checkOffTime,
              owner: false,
            },
          };

          additionalWorkers.push(body);
        }
      }

      leadWorkerRequest
        .concatMap(() => {
          if (additionalWorkers.length === 0) {
            return of([]);
          }

          let requests = additionalWorkers.map(worker => this.siteSessionsService.checkIntoExistingSession(worker));
          return forkJoin(requests);
        })
        .pipe(finalize(() => { this.isFormBeingSubmitted = false; }))
        .subscribe(() => {
          this.checkOnForm.reset();
          this.notificationService.notify('success', 'Success', 'Worker checked into session.');
          this.router.navigate(['/' + this.routeRole + '/sites/', this.selectedSiteId, 'work-orders']);
        });
    });
  }

  // async so multiple can be thrown.
  private async propagateErrors(response) {
    throw response;
  }

  private async validateWorkerBeforeCheckIn(values: any): Promise<boolean> {
    this.selectedWorkers.forEach((worker) => {
      this.validateWorkerSiteRequirements(worker);
    });

    if (values.isSiteSession) {
      await this.validateSiteCompetenciesViaApi();
    } else {
      await Promise.all(
        this.selectedWorkers.map(async (worker, index) => {
          try {
            await this.validateWorkOrderCompetencyChecks(worker, index);
          } catch {
            const workerLabel = index === this.leadWorkerIndex
              ? 'Lead worker'
              : 'Additional Worker';

            this.errorAlerts.push({
              severity: 'error',
              summary: `${workerLabel} competencies could not be validated`,
            });
          }
        }),
      );

      this.selectedWorkers.forEach((worker) => {
        this.validateWorkerAssetCategoryCheck(worker, values.workOrder.asset.assetCategories);
      });
    }

    if (this.errorAlerts.length) {
      this.isFormBeingSubmitted = false;
      return false;
    }

    return true;
  }

  private validateWorkerSiteRequirements(worker: any): void {
    const workerDisplayName = `${worker.workerUser.firstName} ${worker.workerUser.lastName}`;

    if (this.selectedSite.siteInductionCourse && (!worker.inductionForSite || worker.inductionForSite.courseProgress !== TrainingCourseState.Completed)) {
      this.errorAlerts.push({ severity: 'error', summary: `${workerDisplayName} is missing induction` });
    }

    if (!worker.workerUser.activated) {
      this.errorAlerts.push({ severity: 'error', summary: `${workerDisplayName} has not activated their account` });
    }
  }

  private async validateSiteCompetenciesViaApi(): Promise<void> {
    if (!this.selectedWorkers.length) {
      return;
    }

    const requests = this.selectedWorkers.map((worker) =>
      this.competencyService
        .getMissingSiteCompetenciesForWorker(this.selectedSiteId, worker.workerUser.id)
        .pipe(map((missing) => ({ worker, missing }))),
    );

    const results = await forkJoin(requests).toPromise();
    (results || []).forEach(({ worker, missing }) => {
      const workerDisplayName = `${worker.workerUser.firstName} ${worker.workerUser.lastName}`;
      (missing || []).forEach((m) => {
        this.errorAlerts.push({ severity: 'error', summary: `${workerDisplayName} missing site competency: ${m.competencyName}` });
      });
    });
  }

  private async validateWorkOrderCompetencyChecks(worker: any, index: number): Promise<void> {
    const isAdditionalWorker = index !== this.leadWorkerIndex;
    const result = await this.competencyService
      .checkWorkOrderCompetenciesForWorker(
        this.workOrder.id,
        worker.workerUser.id,
        isAdditionalWorker,
      )
      .toPromise();

    if (!result || result.testPassed) {
      return;
    }

    const missingCompetencies = Array.from(new Set(
      (result.competencyTestFailures || [])
        .map((failure) => failure.requiredCompetencyName)
        .filter((name) => !!name),
    ));
    const workerLabel = isAdditionalWorker ? 'Additional Worker' : 'Lead worker';
    const competencyList = missingCompetencies.length
      ? missingCompetencies.join(', ')
      : 'Unknown competency';

    this.errorAlerts.push({
      severity: 'error',
      summary: `${workerLabel} missing competency: ${competencyList}`,
    });
  }

  private validateWorkerAssetCategoryCheck(worker: any, workOrderCategories: any[]): void {
    const workerCategoryIds = worker.assetCategories.map((category) => category.id);
    const hasAtLeastOneCategory = workOrderCategories.some((category) => workerCategoryIds.includes(category.id));

    if (!hasAtLeastOneCategory) {
      const workerDisplayName = `${worker.workerUser.firstName} ${worker.workerUser.lastName}`;
      this.errorAlerts.push({ severity: 'error', summary: `${workerDisplayName} doesn't have the required asset categories` });
    }
  }

  private checkAssetSchedule(values): boolean {
    if (this.allowAssetScheduling) {
      if (!this.assetScheduleSlotCurrentlyAvailable &&
        !values.skipAssetAvailabilityCheck &&
        !values.workOrder.asset.exemptFromAssetScheduling &&
        !values.workOrder.exemptFromAssetScheduling
      ) {
        this.isFormBeingSubmitted = false;
        this.errorAlerts.push({ severity: 'error', summary: 'You must confirm that you are checking-in outside a scheduled window'});
        return false;
      }
    }

    return true;
  }

  private validateCompetencyStatesBeforeCheckIn(values: any): boolean {
    if (!values.isSiteSession) {
      if (this.workOrderLoading) {
        this.errorAlerts.push({ severity: 'error', summary: 'Work order is still loading' });
      } else if (this.workOrderError) {
        this.errorAlerts.push({ severity: 'error', summary: 'Work order could not be loaded' });
      } else if (!values.workOrder) {
        this.errorAlerts.push({ severity: 'error', summary: 'Work order has not been loaded' });
      }

      if (this.workOrderCompetenciesLoading) {
        this.errorAlerts.push({ severity: 'error', summary: 'Work order competencies are still loading' });
      }

      if (this.workOrderCompetenciesError) {
        this.errorAlerts.push({ severity: 'error', summary: 'Work order competencies could not be loaded' });
      }
    }

    const loadingWorkers = this.selectedWorkers.filter((worker) => worker.competenciesLoading);
    const failedWorkers = this.selectedWorkers.filter((worker) => worker.competenciesError);

    if (loadingWorkers.length) {
      const workerNames = loadingWorkers.map((worker) => `${worker.workerUser.firstName} ${worker.workerUser.lastName}`).join(', ');
      this.errorAlerts.push({ severity: 'error', summary: `Worker competencies are still loading for: ${workerNames}` });
    }

    if (failedWorkers.length) {
      const workerNames = failedWorkers.map((worker) => `${worker.workerUser.firstName} ${worker.workerUser.lastName}`).join(', ');
      this.errorAlerts.push({ severity: 'error', summary: `Worker competencies could not be loaded for: ${workerNames}` });
    }

    if (this.errorAlerts.length) {
      this.isFormBeingSubmitted = false;
      return false;
    }

    return true;
  }

  private checkWorkOrderSchedule(values): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!values.workOrder.scheduledStartTime) {
        return resolve(true);
      }

      const dateStr = this.datepipe.transform(values.workOrder.scheduledStartTime, 'dd/MM/yy HH:mm');

      return Swal.fire({
        title: `This work order is scheduled for ${dateStr}`,
        text: `Are you sure you want to check the worker on ?`,
        type: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'confirm-primary',
        },
        confirmButtonText: 'Yes, check on',
      }).then((result) => {
        if (result.value) {
          return resolve(true);
        } else {
          this.isFormBeingSubmitted = false;
          return resolve(false);
        }
      });
    })
  }

  private checkAdditionalWorkers(values): boolean {
    if (values.workOrder.status !== 'OPEN' && values.workOrder.minimumPartySize > this.selectedWorkers.length) {
      this.errorAlerts.push({ severity: 'error', summary: 'This work order has a minimum work party size of ' + values.workOrder.minimumPartySize });
      this.isFormBeingSubmitted = false;
      return false;
    }

    return true;
  }

  private loadSelectedWorkersCompetencies(): Promise<void> {
    return new Promise((resolve) => {
      const requestId = ++this.workerCompetencyRequestId;

      if (!this.selectedWorkers.length) {
        return resolve();
      }

      const siteOwnerId = this.selectedSite.siteOwner.id || null;

      if (!siteOwnerId) {
        this.selectedWorkers.forEach((worker) => {
          worker.competencies = [];
          worker.competenciesLoading = false;
          worker.competenciesError = true;
        });
        return resolve();
      }

      const requests = this.selectedWorkers.map((worker) => {
        return this.competencyService.loadUserCompetencyStatus(worker.workerUser.id, siteOwnerId).pipe(
          map((competencies: UserCompetency[]) => ({
            worker,
            competencies: competencies || [],
            failed: false,
          })),
          catchError(() => of({
            worker,
            competencies: [],
            failed: true,
          })),
        );
      });

      forkJoin(requests).subscribe((results: any[]) => {
        if (requestId !== this.workerCompetencyRequestId) {
          return resolve();
        }

        const failedWorkers = [];

        results.forEach((result) => {
          result.worker.competencies = result.competencies.filter((competency) => competency.status === CompetencyStatus.Approved);
          result.worker.competenciesLoading = false;
          result.worker.competenciesError = result.failed;
          if (result.failed) {
            failedWorkers.push(`${result.worker.workerUser.firstName} ${result.worker.workerUser.lastName}`);
          }
        });

        if (failedWorkers.length) {
          this.notificationService.notify(
            'warning',
            'Competencies unavailable',
            `Could not load competencies for: ${failedWorkers.join(', ')}`,
          );
        }

        resolve();
      });
    });
  }

  public goToCompetencies(worker) {
    this.router.navigate(
      ['/' + this.routeRole + '/workers/' + worker.workerUser.id + '/manage-competencies'],
      { queryParams: { backLink:  `/${this.routeRole}/sites/${this.selectedSiteId}/check-in`, siteOwnerId: this.selectedSite.siteOwner.id } }
    );
  }
}
